import { useEffect } from 'react';
import { useInView } from 'react-intersection-observer';

import { KibButtonNew } from '@chewy/kib-controls-react';
import { KibChipAction } from '@chewy/kib-chips-react';

import { ArrowIcon } from '@src/components/icons/arrow';
import { ChewyCarePlusLogo } from '@src/components/icons/chewy-heath-careplus';
import { CircleImageModifier } from '@src/components/circle-image-modifier';

import WindowService from '@src/services/window.service';
import { chewyCarePlusSupport } from '@src/constants/phone-numbers';
import { useRouteUtils } from '@src/hooks/use-route-utils.hook';

export default function Intro({
  onLinkClicked,
  onFetchPlansClicked,
  onSectionView,
}: {
  onLinkClicked: (
    label: string,
    value: string,
    extraData: Record<string, string>,
  ) => void;
  onFetchPlansClicked: (label: string, value: string) => void;
  onSectionView: (
    label: string,
    value: string,
    extraData?: Record<string, string>,
  ) => void;
}) {
  const [fetchPlansRef, fetchPlansInView] = useInView({
    triggerOnce: true,
  });

  const { getInternalPath } = useRouteUtils();

  useEffect(() => {
    if (fetchPlansInView) {
      onSectionView('topFetchPlans', 'Fetch Plans');
    }
  }, [fetchPlansInView]);

  return (
    <section className="pi-careplus__intro">
      <ChewyCarePlusLogo className="pi-careplus__intro--logo" />

      <div className="pi-careplus__intro--image">
        <CircleImageModifier
          imgSrc="/pet-insurance/img/owner-is-a-pillow.png"
          lazy={true}
        />
      </div>

      <div className="pi-careplus__intro--content">
        <h1 className="pi-careplus__intro--title">
          A Pet-First Approach to <span>Wellness & Insurance</span>
        </h1>
        <p className="pi-careplus__intro--text">
          Give your pet the best possible health coverage with wellness and
          insurance plans designed to meet their needs.
        </p>

        <KibButtonNew
          ref={fetchPlansRef}
          onClick={() => {
            onFetchPlansClicked('topFetchPlans', 'Fetch Plans');
          }}
          size="large"
        >
          Fetch Plans
        </KibButtonNew>

        <div className="pi-careplus__quote--help">
          <span className="pi-careplus__quote--help-text">
            We&apos;re here to help. Call our experts 24/7
          </span>
          <div className="pi-careplus__quote--help-action">
            <KibChipAction
              variant="transparent"
              href={chewyCarePlusSupport.link}
            >
              {chewyCarePlusSupport.phoneNumber}
            </KibChipAction>
          </div>
        </div>

        <hr className="divider" />

        <div className="pi-careplus__quote--skip">
          <span className="pi-careplus__quote--legend">Already enrolled?</span>
          <div className="pi-careplus__quote--action">
            <KibChipAction
              icon={<ArrowIcon />}
              variant="transparent"
              href={getInternalPath(`${WindowService.base}/policies/dashboard`)}
              onClick={() => {
                onLinkClicked('topManageYourPolicy', 'Manage Your Policy', {
                  url: getInternalPath(
                    `${WindowService.base}/policies/dashboard`,
                  ),
                });
              }}
            >
              Manage Your Policy
            </KibChipAction>
          </div>
        </div>
        <div className="pi-careplus__underwriting">
          <a
            className="pi-careplus__underwriting--link"
            target="_blank"
            rel="noopener noreferrer"
            href="/app/content/insurance#Underwriting"
          >
            Underwriting
          </a>
        </div>
      </div>
    </section>
  );
}
